/* eslint-disable  */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { bindActionCreators } from "redux";
import { Boxes, AlertTriangle, Layers, Wallet, ScanLine, ClipboardCheck } from "lucide-react";
import { inventoryActionCreators } from "../services/Actions";
import RenderInventoryPage from "./RenderInventoryPage";
import Loader from "../Components/Loader";
import CediSymbol from "../Components/CediSymbol";
import TablePagination from "./components/TablePagination";

const PAGE_SIZE = 8;

function InventoryDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { GetInventoryItems, GetLowStock } = bindActionCreators(
    inventoryActionCreators,
    dispatch
  );

  const inventory = useSelector((state) => state.inventory);
  const { items, low_stock, isLoading } = inventory;

  const [page, setPage] = useState(1);

  useEffect(() => {
    GetInventoryItems();
    GetLowStock();
  }, []);

  // Reset to first page whenever the low-stock list changes.
  useEffect(() => {
    setPage(1);
  }, [low_stock]);

  const pagedLowStock = (low_stock || []).slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const stats = useMemo(() => {
    const totalItems = items?.length || 0;
    let totalUnits = 0;
    let totalValue = 0;
    (items || []).forEach((i) => {
      totalUnits += Number(i?.stock_on_hand ?? 0);
      totalValue += Number(i?.stock_on_hand ?? 0) * Number(i?.cost_price ?? 0);
    });
    return { totalItems, totalUnits, totalValue, lowCount: low_stock?.length || 0 };
  }, [items, low_stock]);

  const cards = [
    { label: "Tracked Items", value: stats.totalItems, icon: <Boxes size={22} />, color: "emerald", onClick: () => history.push("/inventory/items") },
    { label: "Units in Stock", value: stats.totalUnits, icon: <Layers size={22} />, color: "blue", onClick: () => history.push("/inventory/items") },
    { label: "Low Stock", value: stats.lowCount, icon: <AlertTriangle size={22} />, color: "amber", onClick: () => history.push("/inventory/items") },
    { label: "Stock Value", value: <span><CediSymbol /> {stats.totalValue.toLocaleString()}</span>, icon: <Wallet size={22} />, color: "purple" },
  ];

  const colorMap = {
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30",
    blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/30",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/30",
    purple: "bg-purple-50 text-purple-600 dark:bg-purple-900/30",
  };

  return (
    <RenderInventoryPage title={"Inventory Dashboard"}>
      {isLoading && <Loader />}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={() => history.push("/inventory/scan")}
          className="flex items-center gap-2 text-white bg-emerald-600 hover:bg-emerald-700 font-medium rounded-lg text-sm px-5 py-2.5 shadow"
        >
          <ScanLine size={18} /> Scan Station
        </button>
        <button
          onClick={() => history.push("/inventory/stock-take")}
          className="flex items-center gap-2 text-emerald-700  bg-emerald-50 hover:bg-emerald-100 font-medium rounded-lg text-sm px-5 py-2.5 border border-emerald-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50 shadow"
        >
          <ClipboardCheck size={18} /> Start Stock Take
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c, idx) => (
          <div
            key={idx}
            onClick={c.onClick}
            className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 flex items-center gap-4 ${c.onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
          >
            <div className={`p-3 rounded-xl ${colorMap[c.color]}`}>{c.icon}</div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{c.label}</p>
              <h3 className="text-2xl font-black text-gray-800 dark:text-gray-100">{c.value}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* Low stock table */}
      <section className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <AlertTriangle size={18} className="text-amber-500" />
          <h3 className="font-bold text-gray-800 dark:text-gray-100">Low Stock Alerts</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Available</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Reorder Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {(low_stock || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">
                    All stock levels are healthy 🎉
                  </td>
                </tr>
              ) : (
                pagedLowStock.map((i) => (
                  <tr key={i._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {i?.product?.product_name || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{i?.sku}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                        {i?.available}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">{i?.reorder_level}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          pageSize={PAGE_SIZE}
          total={(low_stock || []).length}
          onPageChange={setPage}
        />
      </section>
    </RenderInventoryPage>
  );
}

export default InventoryDashboard;
